// The base URL of your running Node.js API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
export const SERVER_BASE_URL = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8000';

/**
 * Get the auth token from localStorage for API requests.
 */
function getAuthHeaders() {
    const token = localStorage.getItem('thaman_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/**
 * Wrapper for fetch that automatically includes auth headers.
 */
async function authFetch(url, options = {}) {
    const headers = { ...getAuthHeaders(), ...options.headers };
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
        localStorage.removeItem('thaman_token');
        localStorage.removeItem('thaman_user');
        window.location.href = '/login';
        throw new Error('Session expired');
    }
    return response;
}

/**
 * Fetches all stores from the API.
 * @returns {Promise<Array>} A promise that resolves to an array of store objects.
 */
export const fetchAllStores = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/stores`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching stores:', error);
        throw error; // Re-throw the error so the component can handle it
    }
};

/**
 * Creates a new store by sending form data (including a file) to the API.
 * @param {object} storeData - The data for the new store, including the logo file.
 * @returns {Promise<object>} A promise that resolves to the newly created store object.
 */
export const createStore = async (storeData) => {
    try {
        const formData = new FormData();
        formData.append('english_name', storeData.english_name);
        formData.append('arabic_name', storeData.arabic_name);
        if (storeData.logoFile) {
            formData.append('logo', storeData.logoFile);
        }

        const response = await authFetch(`${API_BASE_URL}/stores`, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error creating store:', error);
        throw error;
    }
};

/**
 * Fetches all vendors from the API.
 * @returns {Promise<Array>} A promise that resolves to an array of vendor objects.
 */
export const fetchAllVendors = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/vendors`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching vendors:', error);
        throw error;
    }
};

/**
 * Creates a new vendor by sending a POST request to the API.
 * @param {object} vendorData - The data for the new vendor (e.g., { english_name, arabic_name }).
 * @returns {Promise<object>} A promise that resolves to the newly created vendor object.
 */
export const createVendor = async (vendorData) => {
    try {
       // Build a FormData object
       const formData = new FormData();
       formData.append('english_name', vendorData.english_name);
       formData.append('arabic_name', vendorData.arabic_name);

       const response = await authFetch(`${API_BASE_URL}/vendors`, {
           method: 'POST',
           body: formData,
       });
       if (!response.ok) throw new Error('Network response was not ok');
       return await response.json();
   } catch (error) {
       console.error('Error creating vendor:', error);
       throw error;
   }
};

/**
 * Fetches all flyers for a specific store ID.
 * @param {number} storeId - The ID of the store.
 * @returns {Promise<Array>} A promise that resolves to an array of flyer objects.
 */
export const fetchFlyersByStore = async (storeId) => {
    try {
        // We use a query parameter to filter by store_id
        const response = await fetch(`${API_BASE_URL}/flyers?store_id=${storeId}`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching flyers for store ${storeId}:`, error);
        throw error;
    }
};

/**
 * Fetches all pages for a specific flyer ID using the nested route.
 * @param {number} flyerId - The ID of the flyer.
 * @returns {Promise<Array>} A promise that resolves to an array of page objects.
 */
export const fetchPagesByFlyer = async (flyerId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/flyers/${flyerId}/pages`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching pages for flyer ${flyerId}:`, error);
        throw error;
    }
};

/**
 * Fetches all offers for a specific vendor ID.
 * @param {number} vendorId - The ID of the vendor.
 * @returns {Promise<Array>} A promise that resolves to an array of offer objects.
 */
export const fetchOffersByVendor = async (vendorId) => {
    try {
        // We use a query parameter to filter by vendor_id
        const response = await fetch(`${API_BASE_URL}/offers?vendor_id=${vendorId}`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching offers for vendor ${vendorId}:`, error);
        throw error;
    }
};

/**
 * Fetches the full, nested category tree from the API.
 * @returns {Promise<Array>} A promise that resolves to the nested category tree.
 */
export const fetchAllCategories = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
};

/**
 * Creates a new category by sending a POST request to the API.
 * @param {object} categoryData - The data for the new category (e.g., { english_name, arabic_name, parent_id }).
 * @returns {Promise<object>} A promise that resolves to the newly created category object.
 */
export const createCategory = async (categoryData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/categories`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(categoryData),
        });
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error('Error creating category:', error);
        throw error;
    }
};

/**
 * Fetches all offers for a specific category ID (including sub-categories).
 * @param {number} categoryId - The ID of the category.
 * @returns {Promise<Array>} A promise that resolves to an array of offer objects.
 */
export const fetchOffersByCategory = async (categoryId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/offers?category_id=${categoryId}`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching offers for category ${categoryId}:`, error);
        throw error;
    }
};

/**
 * Fetches a flat list of all categories from the API.
 * @returns {Promise<Array>} A promise that resolves to a flat array of category objects.
 */
export const fetchAllCategoriesFlat = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/categories/flat`);
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error fetching flat categories:', error);
        throw error;
    }
};

/**
 * Fetches all offers for a specific page ID.
 * @param {number} pageId - The ID of the page.
 * @returns {Promise<Array>} A promise that resolves to an array of offer objects.
 */
export const fetchOffersByPage = async (pageId) => {
    try {
        // We use a query parameter to filter by page_id
        const response = await fetch(`${API_BASE_URL}/offers?page_id=${pageId}`);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching offers for page ${pageId}:`, error);
        throw error;
    }
};

/**
 * Creates a new flyer.
 * @param {object} flyerData - The data for the new flyer.
 * @returns {Promise<object>} A promise that resolves to the newly created flyer.
 */
export const createFlyer = async (formData) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/flyers`, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error creating flyer:', error);
        throw error;
    }
};

/**
 * Creates a new page for a flyer.
 * @param {object} pageData - The data for the new page.
 * @returns {Promise<object>} A promise that resolves to the newly created page.
 */
export const createPage = async (formData) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/pages`, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error creating page:', error);
        throw error;
    }
};

/**
 * Creates a new offer.
 * @param {object} offerData - The data for the new offer.
 * @returns {Promise<object>} A promise that resolves to the newly created offer.
 */
// FIX: This function now accepts FormData directly
export const createOffer = async (offerFormData) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/offers`, {
            method: 'POST',
            body: offerFormData,
        });
        if (!response.ok) {
            // Try to get more specific error info from the server
            const errorData = await response.json();
            throw new Error(errorData.message || 'Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error('Error creating offer:', error);
        throw error;
    }
};

/**
 * Updates an existing store by its ID. Sends form data for potential logo changes.
 * @param {number} storeId - The ID of the store to update.
 * @param {FormData} formData - The form data containing the updated store details.
 * @returns {Promise<object>} A promise that resolves to the server's response.
 */
export const updateStore = async (storeId, storeData) => {
    try {
        const formData = new FormData();
        formData.append('english_name', storeData.english_name);
        formData.append('arabic_name', storeData.arabic_name);
        if (storeData.logoFile) {
            formData.append('logo', storeData.logoFile);
        }

        const response = await authFetch(`${API_BASE_URL}/stores/${storeId}`, {
            method: 'PUT',
            body: formData,
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error updating store ${storeId}:`, error);
        throw error;
    }
};

/**
 * Deletes a store by its ID.
 * @param {number} storeId - The ID of the store to delete.
 * @returns {Promise<object>} A promise that resolves to the server's response.
 */
export const deleteStore = async (storeId) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/stores/${storeId}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error deleting store ${storeId}:`, error);
        throw error;
    }
};

/**
 * Updates an existing vendor by its ID.
 * @param {number} vendorId - The ID of the vendor to update.
 * @param {object} vendorData - The data object with the updated details.
 * @returns {Promise<object>} A promise that resolves to the server's response.
 */
export const updateVendor = async (vendorId, vendorData) => {
    try {
        const formData = new FormData();
        formData.append('english_name', vendorData.english_name);
        formData.append('arabic_name', vendorData.arabic_name);

        const response = await authFetch(`${API_BASE_URL}/vendors/${vendorId}`, {
            method: 'PUT',
            body: formData,
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error updating vendor ${vendorId}:`, error);
        throw error;
    }
};

/**
 * Deletes a vendor by its ID.
 * @param {number} vendorId - The ID of the vendor to delete.
 * @returns {Promise<object>} A promise that resolves to the server's response.
 */
export const deleteVendor = async (vendorId) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/vendors/${vendorId}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error)
 {
        console.error(`Error deleting vendor ${vendorId}:`, error);
        throw error;
    }
};

/**
 * Updates an existing category by its ID.
 * @param {number} categoryId - The ID of the category to update.
 * @param {object} categoryData - The data object with the updated details.
 * @returns {Promise<object>} A promise that resolves to the server's response.
 */
export const updateCategory = async (categoryId, categoryData) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/categories/${categoryId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(categoryData),
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error updating category ${categoryId}:`, error);
        throw error;
    }
};

/**
 * Deletes a category by its ID.
 * @param {number} categoryId - The ID of the category to delete.
 * @returns {Promise<object>} A promise that resolves to the server's response.
 */
export const deleteCategory = async (categoryId) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/categories/${categoryId}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error deleting category ${categoryId}:`, error);
        throw error;
    }
};

/**
 * Fetches the complete details for a single offer by its ID.
 * @param {number} offerId - The ID of the offer to fetch.
 * @returns {Promise<object>} A promise that resolves to the detailed offer object.
 */
export const fetchOfferById = async (offerId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/offers/${offerId}`);
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error fetching offer ${offerId}:`, error);
        throw error;
    }
};

/**
 * Fetches aggregated dashboard statistics from the API.
 */
export const fetchDashboardStats = async () => {
    try {
        const response = await authFetch(`${API_BASE_URL}/stats/dashboard`);
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        throw error;
    }
};

/**
 * Updates the category_id of an offer.
 */
export const updateOfferCategory = async (offerId, categoryId) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/offers/${offerId}/category`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ category_id: categoryId }),
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error updating offer ${offerId} category:`, error);
        throw error;
    }
};

/**
 * Triggers embedding generation for offers that are missing embeddings.
 */
export const backfillEmbeddings = async (limit = 50) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/offers/backfill-embeddings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ limit }),
        });
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error backfilling embeddings:', error);
        throw error;
    }
};

// --- User Management (admin only) ---

export const fetchAllUsers = async () => {
    try {
        const response = await authFetch(`${API_BASE_URL}/users`);
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

export const createUser = async (userData) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to create user');
        }
        return await response.json();
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
};

export const updateUserStatus = async (userId, status) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/users/${userId}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status }),
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to update status');
        }
        return await response.json();
    } catch (error) {
        console.error('Error updating user status:', error);
        throw error;
    }
};

export const resetUserPassword = async (userId, new_password) => {
    try {
        const response = await authFetch(`${API_BASE_URL}/users/${userId}/reset-password`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ new_password }),
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to reset password');
        }
        return await response.json();
    } catch (error) {
        console.error('Error resetting password:', error);
        throw error;
    }
};

export const fetchActivityLog = async ({ user_id, action, limit = 50, offset = 0 } = {}) => {
    try {
        const params = new URLSearchParams();
        if (user_id) params.set('user_id', user_id);
        if (action) params.set('action', action);
        params.set('limit', limit);
        params.set('offset', offset);
        const response = await authFetch(`${API_BASE_URL}/activity-log?${params.toString()}`);
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error('Error fetching activity log:', error);
        throw error;
    }
};